import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";

export type ChunkContentShape =
  | { kind: "table"; rows: Record<string, unknown>[]; columns: string[] }
  | { kind: "object"; obj: Record<string, unknown> }
  | { kind: "longText"; text: string }
  | { kind: "shortText"; text: string }
  | { kind: "empty" };

const LONG_TEXT_THRESHOLD = 200;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const isPrimitive = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean";

export function tryParseJsonContainer(s: string): unknown | undefined {
  const trimmed = s.trim();
  if (!trimmed) return undefined;
  const first = trimmed[0];
  if (first !== "{" && first !== "[") return undefined;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed !== null && typeof parsed === "object") return parsed;
  } catch {
    // not valid JSON — fall back to string rendering
  }
  return undefined;
}

export function detectShape(data: unknown): ChunkContentShape {
  if (data === null || data === undefined || data === "") {
    return { kind: "empty" };
  }

  // Strings that are stringified JSON containers should render as their
  // structured form, not as a wall of escaped text. Try one parse hop.
  if (typeof data === "string") {
    const parsed = tryParseJsonContainer(data);
    if (parsed !== undefined) return detectShape(parsed);
  }

  if (Array.isArray(data)) {
    if (data.length === 0) return { kind: "empty" };
    const objectRows = data.filter(isPlainObject) as Record<string, unknown>[];
    if (objectRows.length > 0 && objectRows.length === data.length) {
      const keyCounts = new Map<string, number>();
      for (const row of objectRows) {
        for (const k of Object.keys(row)) {
          keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
        }
      }
      const columns = Array.from(keyCounts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([k]) => k);
      if (columns.length > 0) {
        return { kind: "table", rows: objectRows, columns };
      }
    }
    return { kind: "object", obj: { items: data } };
  }

  if (isPlainObject(data)) {
    if (Object.keys(data).length === 0) return { kind: "empty" };
    // Tabular envelope: backend chunkers (CSVChunker, HTMLTableChunker) emit
    // ``{metadata, headers, data: [...rows]}``; render the inner array as a
    // table instead of burying it in a "N items" KeyValueGrid node.  Only
    // unwrap if the inner array is genuinely table-shaped (array of objects
    // with consistent-ish keys).
    for (const key of ["data", "rows", "items"]) {
      const inner = (data as Record<string, unknown>)[key];
      if (Array.isArray(inner)) {
        const innerShape = detectShape(inner);
        if (innerShape.kind === "table") return innerShape;
      }
    }
    return { kind: "object", obj: data };
  }

  if (typeof data === "string") {
    if (data.includes("\n") || data.length > LONG_TEXT_THRESHOLD) {
      return { kind: "longText", text: data };
    }
    return { kind: "shortText", text: data };
  }

  return { kind: "shortText", text: String(data) };
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") return value || "—";
  return String(value);
}

const ExpandableText: React.FC<{
  text: string;
  threshold: number;
}> = ({ text, threshold }) => {
  const [expanded, setExpanded] = useState(false);
  const truncatable = text.length > threshold;
  if (!truncatable) {
    return <span>{text}</span>;
  }
  return (
    <Box
      component="span"
      onClick={(e) => {
        e.stopPropagation();
        setExpanded((o) => !o);
      }}
      sx={{
        cursor: "pointer",
        display: "inline",
        textDecoration: "underline dotted",
        textDecorationColor: "rgba(0,0,0,0.25)",
        textUnderlineOffset: 3,
      }}
      title={!expanded ? text : undefined}
    >
      {expanded ? text : `${text.slice(0, threshold).trimEnd()}…`}
    </Box>
  );
};

const VALUE_THRESHOLD = 60;
const LABEL_THRESHOLD = 24;

const ExpandableValue: React.FC<{ value: unknown }> = ({ value }) => (
  <ExpandableText text={formatPrimitive(value)} threshold={VALUE_THRESHOLD} />
);

function summarizeNested(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length === 1 ? "1 item" : `${value.length} items`;
  }
  if (value && typeof value === "object") {
    const n = Object.keys(value as Record<string, unknown>).length;
    return n === 1 ? "1 field" : `${n} fields`;
  }
  return "";
}

const CollapsibleNested: React.FC<{
  label: string;
  value: unknown;
  depth: number;
  defaultOpen: boolean;
}> = ({ label, value, depth, defaultOpen }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          userSelect: "none",
          py: 0.25,
          "&:hover": { color: theme.palette.primary.main },
        }}
      >
        <IconButton
          size="small"
          sx={{ p: 0, color: "inherit" }}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? (
            <ExpandMore fontSize="small" />
          ) : (
            <ChevronRight fontSize="small" />
          )}
        </IconButton>
        <Typography
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "0.75rem",
            textTransform: "capitalize",
            fontWeight: 500,
          }}
        >
          {label.replace(/_/g, " ")}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{ color: theme.palette.text.secondary, ml: 0.5 }}
        >
          ({summarizeNested(value)})
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 0.5,
            ml: 1,
            pl: 1.5,
            borderLeft: `2px solid ${theme.palette.divider}`,
          }}
        >
          <ChunkContentInner data={value} depth={depth + 1} />
        </Box>
      </Collapse>
    </Box>
  );
};

const shouldDefaultOpen = (value: unknown, depth: number): boolean => {
  if (depth === 0) return true;
  if (Array.isArray(value)) return false;
  if (value && typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>);
    return depth === 1 && keys.length <= 6;
  }
  return false;
};

const isNumericColumn = (
  rows: Record<string, unknown>[],
  col: string,
): boolean => {
  let numeric = 0;
  let total = 0;
  for (const r of rows) {
    const v = r[col];
    if (v === null || v === undefined || v === "") continue;
    total += 1;
    if (typeof v === "number") numeric += 1;
  }
  return total > 0 && numeric / total >= 0.7;
};

const DEFAULT_TABLE_ROWS = 3;
const MAX_CELL_WIDTH = 240;

const CompactCell: React.FC<{
  value: unknown;
  numeric?: boolean;
}> = ({ value, numeric }) => {
  const [expanded, setExpanded] = useState(false);
  if (isPrimitive(value)) {
    const text = formatPrimitive(value);
    const truncatable = text.length > 28;
    return (
      <Typography
        component="span"
        title={truncatable && !expanded ? text : undefined}
        onClick={(e) => {
          if (!truncatable) return;
          e.stopPropagation();
          setExpanded((o) => !o);
        }}
        sx={{
          display: "inline-block",
          maxWidth: expanded ? "none" : MAX_CELL_WIDTH,
          overflow: expanded ? "visible" : "hidden",
          textOverflow: expanded ? "clip" : "ellipsis",
          whiteSpace: expanded ? "normal" : "nowrap",
          wordBreak: expanded ? "break-word" : "normal",
          verticalAlign: "bottom",
          fontFamily: numeric ? "monospace" : "inherit",
          fontSize: "0.8125rem",
          cursor: truncatable ? "pointer" : "default",
          textDecoration: truncatable ? "underline dotted" : "none",
          textDecorationColor: "rgba(0,0,0,0.2)",
          textUnderlineOffset: 3,
        }}
      >
        {text}
      </Typography>
    );
  }
  // Non-primitive cell: show summary
  return (
    <Typography
      component="span"
      sx={{
        fontSize: "0.75rem",
        fontStyle: "italic",
        color: "text.secondary",
      }}
    >
      ({summarizeNested(value)})
    </Typography>
  );
};

const TableRenderer: React.FC<{
  rows: Record<string, unknown>[];
  columns: string[];
  depth: number;
}> = ({ rows, columns }) => {
  const theme = useTheme();
  const numericCols = new Set(columns.filter((c) => isNumericColumn(rows, c)));
  const [showAll, setShowAll] = useState(false);
  const visibleRows =
    showAll || rows.length <= DEFAULT_TABLE_ROWS
      ? rows
      : rows.slice(0, DEFAULT_TABLE_ROWS);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        {rows.length} {rows.length === 1 ? "row" : "rows"} · {columns.length}{" "}
        {columns.length === 1 ? "column" : "columns"}
      </Typography>
      <TableContainer
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          maxHeight: 480,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c}
                  align={numericCols.has(c) ? "right" : "left"}
                  sx={{
                    fontWeight: 600,
                    bgcolor: theme.palette.grey[50],
                    textTransform: "capitalize",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.replace(/_/g, " ")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, i) => (
              <TableRow key={i} hover>
                {columns.map((c) => (
                  <TableCell
                    key={c}
                    align={numericCols.has(c) ? "right" : "left"}
                    sx={{
                      fontSize: "0.8125rem",
                      verticalAlign: "top",
                      maxWidth: MAX_CELL_WIDTH,
                    }}
                  >
                    <CompactCell value={row[c]} numeric={numericCols.has(c)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hiddenCount > 0 && (
        <ButtonBase
          onClick={() => setShowAll(true)}
          aria-expanded={false}
          sx={{
            mt: 0.5,
            color: theme.palette.primary.main,
            fontSize: "0.75rem",
            justifyContent: "flex-start",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Show all {rows.length} rows ({hiddenCount} more)
        </ButtonBase>
      )}
      {showAll && rows.length > DEFAULT_TABLE_ROWS && (
        <ButtonBase
          onClick={() => setShowAll(false)}
          aria-expanded={true}
          sx={{
            mt: 0.5,
            color: theme.palette.text.secondary,
            fontSize: "0.75rem",
            justifyContent: "flex-start",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Show fewer
        </ButtonBase>
      )}
    </Box>
  );
};

const KeyValueGrid: React.FC<{
  obj: Record<string, unknown>;
  depth: number;
}> = ({ obj, depth }) => {
  const theme = useTheme();
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data
      </Typography>
    );
  }

  const primitiveEntries = entries.filter(([, v]) => isPrimitive(v));
  const nestedEntries = entries.filter(([, v]) => !isPrimitive(v));

  return (
    <Stack spacing={1}>
      {primitiveEntries.length > 0 && (
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 35%) minmax(0, 1fr)",
            columnGap: 1.5,
            rowGap: 0.5,
            m: 0,
            fontSize: "0.8125rem",
            alignItems: "baseline",
          }}
        >
          {primitiveEntries.map(([key, value]) => (
            <React.Fragment key={key}>
              <Typography
                component="dt"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  fontWeight: 500,
                  textAlign: "left",
                  m: 0,
                  minWidth: 0,
                }}
              >
                <ExpandableText
                  text={key.replace(/_/g, " ")}
                  threshold={LABEL_THRESHOLD}
                />
              </Typography>
              <Typography
                component="dd"
                sx={{
                  m: 0,
                  fontSize: "0.8125rem",
                  color: theme.palette.text.primary,
                  wordBreak: "break-word",
                  minWidth: 0,
                }}
              >
                <ExpandableValue value={value} />
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}
      {nestedEntries.map(([key, value]) => (
        <CollapsibleNested
          key={key}
          label={key}
          value={value}
          depth={depth}
          defaultOpen={shouldDefaultOpen(value, depth)}
        />
      ))}
    </Stack>
  );
};

const TextBlock: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderLeft: `4px solid ${theme.palette.divider}`,
        pl: 2,
        py: 0.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "inherit",
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
        lineHeight: 1.6,
      }}
    >
      {text}
    </Box>
  );
};

const ShortText: React.FC<{ text: string }> = ({ text }) => (
  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
    {text}
  </Typography>
);

const EmptyState: React.FC = () => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ fontStyle: "italic" }}
  >
    No content
  </Typography>
);

const MAX_DEPTH = 4;

const ChunkContentInner: React.FC<{ data: unknown; depth: number }> = ({
  data,
  depth,
}) => {
  if (depth > MAX_DEPTH) {
    return (
      <Typography variant="caption" color="text.secondary">
        (deeply nested —{" "}
        {Array.isArray(data) ? `${data.length} items` : "object"})
      </Typography>
    );
  }
  const shape = detectShape(data);

  return (
    <Stack>
      {shape.kind === "table" && (
        <TableRenderer
          rows={shape.rows}
          columns={shape.columns}
          depth={depth}
        />
      )}
      {shape.kind === "object" && (
        <KeyValueGrid obj={shape.obj} depth={depth} />
      )}
      {shape.kind === "longText" && <TextBlock text={shape.text} />}
      {shape.kind === "shortText" && <ShortText text={shape.text} />}
      {shape.kind === "empty" && <EmptyState />}
    </Stack>
  );
};

const ChunkContent: React.FC<{ data: unknown }> = ({ data }) => (
  <ChunkContentInner data={data} depth={0} />
);

export default ChunkContent;
